import { Person, Save } from '@mui/icons-material';
import {
	Autocomplete,
	Box,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	TextField,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import {
	getIndicacaoUsuarios,
	IndicacaoUsuarioBusca,
	putIndicacaoRegra,
} from '../../../services/indicacao';
import { useToastStore } from '../../../stores';
import { parseQuantidadeOpcional, quantidadeOpcionalValida } from '../indicacao.utils';
import { IndicacaoButton } from './IndicacaoButton';
import { SectionHeader } from './SectionHeader';

export function RegraIndicadorPanel() {
	const { setToast } = useToastStore();
	const [opcoes, setOpcoes] = useState<IndicacaoUsuarioBusca[]>([]);
	const [usuario, setUsuario] = useState<IndicacaoUsuarioBusca | null>(null);
	const [buscando, setBuscando] = useState(false);
	const [salvando, setSalvando] = useState(false);
	const [tipoRegra, setTipoRegra] = useState('');
	const [qtdDias, setQtdDias] = useState('');
	const [qtdVezes, setQtdVezes] = useState('');
	const buscaRef = useRef(0);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(
		() => () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		},
		[]
	);

	const buscar = (termo: string) => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			const carga = ++buscaRef.current;
			setBuscando(true);
			void getIndicacaoUsuarios(termo)
				.then((response) => {
					if (carga !== buscaRef.current) return;
					setOpcoes(response?.success ? response.data ?? [] : []);
				})
				.catch(() => {
					if (carga !== buscaRef.current) return;
					setOpcoes([]);
				})
				.finally(() => {
					if (carga === buscaRef.current) setBuscando(false);
				});
		}, 300);
	};

	const selecionar = (escolhido: IndicacaoUsuarioBusca | null) => {
		setUsuario(escolhido);
		setTipoRegra(escolhido?.tipo_regra === 'DIAS' || escolhido?.tipo_regra === 'VEZES' ? escolhido.tipo_regra : '');
		setQtdDias(escolhido?.qtd_dias != null ? String(escolhido.qtd_dias) : '');
		setQtdVezes(escolhido?.qtd_vezes != null ? String(escolhido.qtd_vezes) : '');
	};

	const salvar = async () => {
		if (!usuario) {
			setToast({ message: 'Escolha o indicador.', variant: 'error' });
			return;
		}
		if (tipoRegra !== 'DIAS' && tipoRegra !== 'VEZES') {
			setToast({ message: 'Informe se a regra é por dias ou pelas primeiras compras.', variant: 'error' });
			return;
		}
		const dias = parseQuantidadeOpcional(qtdDias);
		const vezes = parseQuantidadeOpcional(qtdVezes);
		if (!quantidadeOpcionalValida(dias)) {
			setToast({ message: 'Informe os dias vazio ou maior ou igual a 1.', variant: 'error' });
			return;
		}
		if (!quantidadeOpcionalValida(vezes)) {
			setToast({ message: 'Informe as compras vazio ou maior ou igual a 1.', variant: 'error' });
			return;
		}

		setSalvando(true);
		try {
			const response = await putIndicacaoRegra(usuario.id, {
				tipo_regra: tipoRegra,
				qtd_dias: dias,
				qtd_vezes: vezes,
			});
			if (!response?.success || !response.data) {
				setToast({
					message: response?.message || 'Não foi possível salvar a regra do indicador.',
					variant: 'error',
				});
				return;
			}
			setUsuario({
				...usuario,
				tipo_regra: response.data.tipo_regra,
				qtd_dias: response.data.qtd_dias,
				qtd_vezes: response.data.qtd_vezes,
			});
			setToast({ message: response.message || 'Regra do indicador salva.', variant: 'success' });
		} catch {
			setToast({ message: 'Não foi possível salvar a regra do indicador.', variant: 'error' });
		} finally {
			setSalvando(false);
		}
	};

	return (
		<Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: '1px solid #f1f5f9', height: '100%' }}>
			<SectionHeader
				icon={<Person />}
				titulo="Regra do indicador"
				descricao="Vale para os ganhos ainda não avaliados. Por dias, o prazo conta do cadastro da indicação."
				acao={
					<IndicacaoButton
						label={salvando ? 'Salvando...' : 'Salvar'}
						icon={<Save />}
						loading={salvando}
						disabled={!usuario}
						onClick={() => void salvar()}
					/>
				}
			/>
			<Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1.4fr 1fr 1fr 1fr' }} gap={1.5} mt={2.5}>
				<Autocomplete
					options={opcoes}
					loading={buscando}
					value={usuario}
					onOpen={() => buscar('')}
					onChange={(_, value) => selecionar(value)}
					onInputChange={(_, value, reason) => {
						if (reason === 'input' || reason === 'clear') buscar(reason === 'clear' ? '' : value);
					}}
					getOptionLabel={(option) => option.nome}
					isOptionEqualToValue={(option, value) => option.id === value.id}
					renderInput={(params) => <TextField {...params} label="Indicador" placeholder="Nome do usuário" />}
				/>
				<FormControl fullWidth disabled={!usuario || salvando}>
					<InputLabel id="tipo-regra-indicador">Modo</InputLabel>
					<Select
						labelId="tipo-regra-indicador"
						label="Modo"
						value={tipoRegra}
						onChange={(event) => setTipoRegra(event.target.value)}
					>
						<MenuItem value="DIAS">Por dias</MenuItem>
						<MenuItem value="VEZES">Pelas primeiras compras</MenuItem>
					</Select>
				</FormControl>
				<TextField
					label="Dias"
					value={qtdDias}
					disabled={!usuario || salvando}
					placeholder="Sem limite"
					onChange={(event) => setQtdDias(event.target.value.replace(/\D/g, ''))}
				/>
				<TextField
					label="Compras"
					value={qtdVezes}
					disabled={!usuario || salvando}
					placeholder="Sem limite"
					onChange={(event) => setQtdVezes(event.target.value.replace(/\D/g, ''))}
				/>
			</Box>
		</Paper>
	);
}
